import { useCallback, useEffect, useRef, useState } from 'react';
import type { CalendarEventFile } from '@todo/shared';
import { calendarApi } from '../api/calendar.api';
import { ApiError } from '../api/client';

/**
 * Documents on a meeting - the agenda, the deck, the papers to read first.
 *
 * ONLY ON A SAVED MEETING. A file needs an event id to belong to, and the
 * id does not exist until the meeting is created, so the create form says
 * "save first" rather than buffering uploads client-side and replaying
 * them - a replay that half-fails leaves nobody able to say what was
 * stored.
 *
 * LOADED ON MOUNT, NEVER CACHED ACROSS SESSIONS. Each row's downloadUrl is
 * a short-lived SAS link; holding the list for an hour would leave a panel
 * full of links that 403.
 *
 * The owner uploads and deletes; attendees see and download. The server
 * decides that - `canEdit` here only shapes the UI, and sending an upload
 * without it still fails with 403.
 */

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface MeetingFilesPanelProps {
  eventId: string;
  /** True for the meeting owner - the only person the API lets write. */
  canEdit: boolean;
}

export function MeetingFilesPanel({ eventId, canEdit }: MeetingFilesPanelProps) {
  const [files, setFiles] = useState<CalendarEventFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setFiles(await calendarApi.listFiles(eventId));
      setError(null);
    } catch (err) {
      /* Shown, not swallowed. A silent catch here is how "the files are
         missing" turns into an afternoon of guessing. */
      setError(err instanceof ApiError ? err.message : 'Could not load the files.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      setFiles(await calendarApi.uploadFile(eventId, file));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${file.name} did not upload.`);
    } finally {
      setBusy(false);
      // Let the same file be picked again after a failure.
      if (picker.current) picker.current.value = '';
    }
  }

  async function remove(file: CalendarEventFile) {
    setBusy(true);
    setError(null);
    try {
      setFiles(await calendarApi.deleteFile(eventId, file.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${file.fileName} was not removed.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Files
        </p>
        {files.length > 0 && (
          <span className="text-xs font-semibold text-slate-600">{files.length}</span>
        )}
      </div>

      {loading ? (
        <p className="mt-2 text-xs text-slate-500">Loading…</p>
      ) : (
        <>
          {files.length === 0 && (
            <p className="mt-2 text-xs text-slate-500">
              {canEdit
                ? 'Nothing attached yet. Everyone invited can download what you add.'
                : 'Nothing attached to this meeting.'}
            </p>
          )}

          <ul className="mt-2 space-y-1.5">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
              >
                <div className="min-w-0">
                  {/* A plain link: the SAS URL carries its own auth, so no
                      header and no fetch - the browser downloads it. */}
                  <a
                    href={file.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium text-brand-700 hover:underline"
                  >
                    {file.fileName}
                  </a>
                  <p className="truncate text-xs text-slate-500">
                    {prettySize(file.sizeBytes)} · {file.uploadedByName}
                  </p>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void remove(file)}
                    className="shrink-0 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>

          {canEdit && (
            <>
              <input
                ref={picker}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => picker.current?.click()}
                className="mt-2 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-brand-400 hover:text-brand-700 disabled:opacity-50"
              >
                {busy ? 'Uploading…' : 'Add a file'}
              </button>
            </>
          )}
        </>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
